// import { useLocation, useNavigate } from "react-router-dom";
// import Icons from "../../../ui/icons/index.js";
// import { useAuth } from "../../../../core/provider/Auth/index.js";

// type TopbarProps = {
//     action?: any;
// };

// const routeTitles: Record<string, string> = {
//     "/admin/dashboard": "داشبورد",
//     "/admin/users": "کاربران",
//     "/admin/transactions": "تراکنش‌ها",
//     "/admin/settings": "تنظیمات",
//     "/admin/reports": "گزارش‌ها",
// };

// export default function Topbar({ action }: TopbarProps) {
//     const navigate = useNavigate();
//     const { pathname } = useLocation();
//     const { user } = useAuth?.() ?? {};

//     const title =
//         routeTitles[pathname] ??
//         Object.entries(routeTitles).find(([key]) =>
//             pathname.startsWith(key + "/")
//         )?.[1] ??
//         "پنل مدیریت";

//     const breadcrumbs = pathname.split("/").filter(Boolean);

//     return (
//         <header className="flex items-center justify-between gap-4 px-5 bg-white border-b h-14 border-neutral-200 shrink-0">
//             {/* === Right: Breadcrumb + Title === */}

//             {/* === Center: Action from provider === */}
//             {action && (
//                 <div className="flex items-center justify-center flex-1 gap-2">
//                     {action}
//                 </div>
//             )}

//             {/* === Left: User === */}
//             <div className="flex items-center gap-2">
//                 <button
//                     onClick={() => navigate("/admin/profile")}
//                     className="flex items-center gap-2 p-1 pr-2 transition rounded-xl hover:bg-neutral-100"
//                 >
//                     <div className="flex flex-col items-end leading-tight">
//                         <span className="text-xs font-semibold text-neutral-800">
//                             {user?.name ?? "مدیر سیستم"}
//                         </span>
//                         <span className="text-[10px] text-neutral-400">
//                             {user?.role ?? "Admin"}
//                         </span>
//                     </div>
//                     <div className="flex items-center justify-center w-8 h-8 text-xs font-bold text-white rounded-full bg-brand-600">
//                         {(user?.name?.[0] ?? "A").toUpperCase()}
//                     </div>
//                     <Icons
//                         name="arrow_down"
//                         className="w-3 h-3 text-neutral-400"
//                     />
//                 </button>
//             </div>
//         </header>
//     );
// }
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import {
    ChevronDown,
    LogOut,
    User as UserIcon,
    Settings,
} from "lucide-react";
import { useAuth } from "../../../../core/provider/Auth/index.js";
import { useClearCache } from "../../../../core/hooks/useClearCache.js";
import { toastify } from "../../../ui/Toasts/index.js";

type TopbarProps = {
    action?: any;
};

const routeTitles: Record<string, string> = {
    "/admin/dashboard": "داشبورد",
    "/admin/users": "کاربران",
    "/admin/transactions": "تراکنش‌ها",
    "/admin/settings": "تنظیمات",
    "/admin/reports": "گزارش‌ها",
};

export default function Topbar({ action }: TopbarProps) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { user, logout, refreshUser } = useAuth?.() ?? {};

    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const { clearAllCache } = useClearCache();

    // ===== Logout =====
    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            clearAllCache?.();
            logout?.();
            toastify("success", "با موفقیت از حساب خود خارج شدید");
            localStorage.removeItem("token");
            sessionStorage.clear();
            navigate("/login", { replace: true });
            await refreshUser?.();
        } catch (error) {
            console.error("خطا در خروج:", error);
            toastify("error", "خطا در خروج از حساب کاربری");
        } finally {
            setIsLoggingOut(false);
            setIsUserMenuOpen(false);
        }
    };

    // ===== Close dropdown on outside click =====
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node)
            ) {
                setIsUserMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // ===== Close dropdown on route change =====
    useEffect(() => {
        setIsUserMenuOpen(false);
    }, [pathname]);

    return (
        <header className="flex items-center justify-between gap-4 px-5 bg-white border-b h-14 border-neutral-200 shrink-0">
            {/* === Center: Action from provider === */}
            {action && (
                <div className="flex items-center justify-center flex-1 gap-2">
                    {action}
                </div>
            )}

            {/* === Left: User Menu === */}
            <div className="relative" ref={menuRef}>
                <button
                    onClick={() => setIsUserMenuOpen((prev) => !prev)}
                    className={`flex items-center gap-2 p-1 pr-2 transition rounded-xl hover:bg-neutral-100 ${isUserMenuOpen ? "bg-neutral-100" : ""
                        }`}
                >
                    <div className="flex flex-col items-end leading-tight">
                        <span className="text-xs font-semibold text-neutral-800">
                            {"ادمین سیستم"}
                        </span>
                        <span className="text-[10px] text-neutral-400">
                            {"Admin"}
                        </span>
                    </div>
                    <div className="flex items-center justify-center w-8 h-8 text-xs font-bold text-white rounded-full bg-brand-600">
                        {("AD").toUpperCase()}
                    </div>
                    <ChevronDown
                        className={`w-3.5 h-3.5 text-neutral-400 transition-transform duration-200 ${isUserMenuOpen ? "rotate-180" : "rotate-0"
                            }`}
                    />
                </button>

                {/* ===== Dropdown ===== */}
                {isUserMenuOpen && (
                    <div className="absolute left-0 z-50 mt-2 overflow-hidden bg-white border shadow-lg top-full w-56 rounded-xl border-neutral-200 shadow-neutral-200/50 animate-in fade-in slide-in-from-top-2 duration-150">
                        {/* User info */}
                        <div className="flex items-center gap-3 px-3 py-3 border-b border-neutral-100 bg-neutral-50/60">
                            <div className="flex items-center justify-center w-9 h-9 text-xs font-bold text-white rounded-full bg-gradient-to-br from-brand-500 to-brand-700 shrink-0">
                                {("AD").toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[12px] font-semibold text-neutral-800 truncate">
                                    {"ادمین سیستم"}
                                </p>
                                <p className="text-[10px] text-neutral-400 truncate mt-0.5">
                                    {user?.role ?? "Admin"}
                                </p>
                            </div>
                        </div>

                        {/* Actions */}
                        {/* <div className="p-1">
                            <button
                                onClick={() => {
                                    setIsUserMenuOpen(false);
                                    navigate("/admin/profile");
                                }}
                                className="flex items-center w-full gap-2.5 px-3 py-2 text-[12px] font-medium transition rounded-lg text-neutral-700 hover:bg-neutral-100"
                            >
                                <UserIcon className="w-3.5 h-3.5 text-neutral-500" />
                                <span>پروفایل من</span>
                            </button>

                            <button
                                onClick={() => {
                                    setIsUserMenuOpen(false);
                                    navigate("/admin/settings");
                                }}
                                className="flex items-center w-full gap-2.5 px-3 py-2 text-[12px] font-medium transition rounded-lg text-neutral-700 hover:bg-neutral-100"
                            >
                                <Settings className="w-3.5 h-3.5 text-neutral-500" />
                                <span>تنظیمات</span>
                            </button>
                        </div> */}

                        {/* Logout */}
                        <div className="p-1 border-t border-neutral-100">
                            <button
                                onClick={handleLogout}
                                disabled={isLoggingOut}
                                className="flex items-center w-full gap-2.5 px-3 py-2 text-[12px] font-medium transition rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <LogOut className="w-3.5 h-3.5" />
                                <span>
                                    {isLoggingOut
                                        ? "در حال خروج..."
                                        : "خروج از حساب"}
                                </span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
}